import React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { productVaidationSchema } from '../../../validations/LineItemValidation';
import { Divider, Grid, Paper, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import ItemCodeSelect from '../../../components/form/ItemCodeSelect';
import UOMSelect from '../../../components/form/UOMSelect';
import NumberOrPercentageInput from '../../../components/NumberOrPercentageInput';
import TaxTypeSelect from '../../../components/form/TaxTypeSelect';
import { TAX_TYPES } from '../../../utils/taxTypes';
import Button from '@mui/material/Button';
import CustomNumberInput from '../../../components/CustomNumberInput';

const defaultState = {
  item_code: null,
  unit_of_measure: null,
  description: '',
  discount_amount: 0,
  charge_amount: 0,
  tax_amount: 0,
  quantity: 1,
  unit_rate: 1,
  taxes: [
    {
      amount: 0,
      type: 'percentage',
      value: 0,
      category: null,
    },
  ],
};

const ProductsForm = ({ submitForm, handleClose, initialData = defaultState, readOnly = false }) => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialData,
    resolver: yupResolver(productVaidationSchema),
  });

  const {
    fields: taxFields,
    append: appendTax,
    remove: removeTax,
  } = useFieldArray({
    control: control,
    name: 'taxes',
  });

  const lineItemsData = watch();
  delete lineItemsData.product;

  const handleSave = (data) => {
    const itemData = {
      ...data,
      item_code: data.item_code.key,
      unit_of_measure: data.unit_of_measure.key,
      taxes: data.taxes.map(({ category, ...rest }) => ({ category: category.key, ...rest })),
    };
    delete itemData.product;
    submitForm(itemData);
  };

  const handleSinglePopupFormSubmit = (data) => {
    handleSave(data);
    handleClose();
  };

  const handleMultiplePopupFormSubmit = (data) => {
    handleSave(data);
    reset(defaultState);
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: '1rem', overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Item Description */}
            <Grid size={{ xs: 12 }}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="description"
                    label="Description"
                    variant="outlined"
                    name={'description'}
                    multiline
                    fullWidth
                    {...field}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    InputProps={{ readOnly }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <ItemCodeSelect
                label={'Classification Code'}
                control={control}
                errors={errors}
                name="item_code"
                readOnly={readOnly}
              />
            </Grid>

            {/* Unit of Measure */}
            <Grid size={{ xs: 4 }}>
              <UOMSelect control={control} errors={errors} name="unit_of_measure" readOnly={readOnly} />
            </Grid>

            {/* Quantity */}
            <Grid size={{ xs: 4 }}>
              <CustomNumberInput
                control={control}
                errors={errors}
                name="quantity"
                readOnly={readOnly}
                label="Quantity"
              />
            </Grid>

            {/* Rate */}
            <Grid size={{ xs: 4 }}>
              <CustomNumberInput
                control={control}
                errors={errors}
                name="unit_rate"
                readOnly={readOnly}
                label="Unit Rate"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            Taxes
            <Divider></Divider>
          </Typography>

          {taxFields.map((charge, taxIndex) => (
            <Grid key={charge.id} container spacing={2} rowspacing={2}>
              <Grid size={{ xs: 6 }}>
                <TaxTypeSelect
                  control={control}
                  errors={errors}
                  name={`taxes.${taxIndex}.category`}
                  setValue={setValue}
                  index={taxIndex}
                  readOnly={readOnly}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <NumberOrPercentageInput
                  value={`taxes.${taxIndex}.value`}
                  type={`taxes.${taxIndex}.type`}
                  id={'taxes-value'}
                  label={'Taxes Value'}
                  control={control}
                  setValue={setValue}
                  disabled={watch(`taxes.${taxIndex}.category.key`) === TAX_TYPES.NotApplicable.Code}
                  error={!!errors.taxes?.[taxIndex]?.value}
                  helperText={errors.taxes?.[taxIndex]?.value?.message}
                  readOnly={readOnly}
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      </Paper>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {!readOnly && (
          <>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleMultiplePopupFormSubmit)}
            >
              Save & Add new
            </Button>
            <Button
              id="save-product"
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleSinglePopupFormSubmit)}
            >
              Save
            </Button>
          </>
        )}
      </Box>
    </>
  );
};

export default ProductsForm;
